import DoneIcon from '@mui/icons-material/Done'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Fab,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import React, { createRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactPlayer from 'react-player'
import screenfull from 'screenfull'

import pathUrl from '../api/pathURL'
import LoadingDocuments from '../components/loading/Documents'
import useFetch from '../hooks/use-fetch'
import { LogType } from '../types/interface'
import { createLog } from '../utils/log-action'

interface TrainingType {
  id: number
  title: string
  type: 'guide' | 'video'
  url: string
  watched: boolean
}

interface MasterPlayerProps {
  data: TrainingType
}

const MasterPlayer: React.FC<MasterPlayerProps> = ({ data }) => {
  const [open, setOpen] = useState<boolean>(false)
  const [status, setStatus] = useState<boolean>(data.watched)
  const playerRef = createRef<HTMLDivElement>()
  const { t } = useTranslation()

  const handleCollapse = () => setOpen(!open)

  const handleClickFullscreen = () => {
    if (playerRef.current && screenfull.isEnabled) {
      screenfull.request(playerRef.current)
    }
  }

  const onFinished = async (payload: LogType) => {
    await createLog(payload).then(() => {
      setStatus(true)
      handleCollapse()
    })
  }

  return (
    <>
      <ListItemButton onClick={handleCollapse}>
        <ListItemText
          primary={
            <Typography color="text.primary" variant="subtitle1">
              {t('training.title', { data })}
            </Typography>
          }
          secondary={
            <Chip
              color={status ? 'success' : 'warning'}
              component="span"
              label={status ? t('training.completed') : t('training.pending')}
              size="small"
            />
          }
        />
        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </ListItemButton>
      <Collapse
        in={open}
        sx={{ position: 'relative' }}
        timeout="auto"
        unmountOnExit
      >
        <div ref={playerRef} style={{ height: '100%', width: '100%' }}>
          <ReactPlayer
            config={{
              youtube: {
                playerVars: {
                  cc_lang_pref: 'en',
                  cc_load_policy: 1,
                  disablekb: 1,
                  fs: 1,
                  modestbranding: 1,
                  origin: 'https://www.youtube.com',
                },
              },
            }}
            height="100%"
            onEnded={() =>
              onFinished({
                action: 'finishVideo',
                description: data.title,
                module: 'Training',
                resource_id: data.id,
              })
            }
            playsinline
            style={{
              minHeight: '360px',
            }}
            url={data.url}
            width="100%"
          />
        </div>
        <Fab
          aria-label="fullscreen"
          color="primary"
          onClick={handleClickFullscreen}
          sx={{
            backgroundColor: 'rgba(0,0,0,0.3)',
            bottom: 16,
            position: 'absolute',
            right: 16,
          }}
        >
          <FullscreenIcon fontSize="medium" />
        </Fab>
      </Collapse>
      <Divider />
    </>
  )
}

const GuideItem: React.FC<{ data: TrainingType }> = ({ data }) => {
  const [status, setStatus] = useState<boolean>(data.watched)
  const { t } = useTranslation()

  const handleMarkAsComplete = async () => {
    try {
      await createLog({
        action: 'completeGuide',
        description: data?.title,
        module: 'Training',
        resource_id: data?.id,
      })
      setStatus(true)
      await createLog({
        action: 'finishGuide',
        description: data?.title,
        module: 'Training',
        resource_id: data?.id,
      })
    } catch (error) {
      console.error('Error completing guide:', error)
    }
  }
  const guideUrl = `${import.meta.env.VITE_TRAINING_GUIDE_CLOUDFRONT_URL}${data.url}`
  return (
    <>
      <ListItemButton component="a" href={guideUrl} target="_blank">
        <ListItemText
          primary={
            <Typography color="text.primary" variant="subtitle1">
              {data.title}
            </Typography>
          }
          secondary={
            <Chip
              color={status ? 'success' : 'warning'}
              component="span"
              label={status ? t('training.completed') : t('training.pending')}
              size="small"
            />
          }
        />
      </ListItemButton>
      {!status && (
        // Centering the button
        <Box display="flex" justifyContent="center" mb={2} mt={2}>
          <Button
            onClick={handleMarkAsComplete}
            startIcon={<DoneIcon />}
            style={{
              backgroundColor: status ? 'green' : 'grey',
              color: 'white',
            }} // Use backgroundColor for button color
            variant="contained"
          >
            {t('training.mark_as_complete')}
          </Button>
        </Box>
      )}
      <Divider sx={{ mt: 2 }} />
    </>
  )
}

const Training: React.FC = () => {
  const { data, error, isLoading } = useFetch(pathUrl.getTraining)
  const [tabIndex, setTabIndex] = useState(0)
  const { t } = useTranslation()

  if (isLoading) {
    return <LoadingDocuments />
  }

  if (error) {
    return <> {t('errors.something_wrong')} </>
  }

  const trainingData = data as {
    guides: TrainingType[]
    videos: TrainingType[]
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue)
  }

  return (
    <Grid alignItems="center" container direction="column" sx={{ p: 2 }}>
      <Grid item>
        <Tabs
          aria-label="training tabs"
          onChange={handleTabChange}
          value={tabIndex}
        >
          <Tab label={t('training.videos')} />
          <Tab label={t('training.guides')} />
        </Tabs>
      </Grid>
      <Grid hidden={tabIndex !== 0} item>
        <List>
          {trainingData.videos.map((item: TrainingType) => (
            <MasterPlayer data={item} key={item.id} />
          ))}
        </List>
      </Grid>
      <Grid hidden={tabIndex !== 1} item sx={{ p: 2 }}>
        <List>
          {trainingData.guides.map((item: TrainingType) => (
            <GuideItem data={item} key={item.id} />
          ))}
        </List>
      </Grid>
    </Grid>
  )
}

export default Training
